import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import type { Group } from 'three';
import { COLORS, IsometricBlock, IsometricPillar, IsometricArch, useGameStore } from '@hollowveil/shared';
import type { ModelProps, ModelRegistry } from './types';

export function CubeModel({ world, hovered, interactive, properties }: ModelProps) {
  return (
    <IsometricBlock
      position={[0, 0.5, 0]}
      size={[1, 1, 1]}
      world={world}
      variant={(properties.variant as 'stone' | 'accent' | 'highlight' | 'wood') || 'stone'}
      interactive={interactive}
      hovered={hovered}
    />
  );
}

export function PillarModel({ world, properties, opacity = 1 }: ModelProps) {
  return (
    <IsometricPillar
      position={[0, 0, 0]}
      height={(properties.height as number) || 3}
      world={world}
      opacity={opacity}
    />
  );
}

export function ArchModel({ world, opacity = 1 }: ModelProps) {
  return (
    <IsometricArch
      position={[0, 0, 0]}
      world={world}
      opacity={opacity}
    />
  );
}

export function DoorModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;
  return (
    <group>
      {/* Frame */}
      <mesh position={[-0.55, 1.25, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.1, 2.5, 0.3]} />
        <meshStandardMaterial color={colors.stone} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.55, 1.25, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.1, 2.5, 0.3]} />
        <meshStandardMaterial color={colors.stone} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 2.6, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.2, 0.2, 0.3]} />
        <meshStandardMaterial color={colors.stone} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Door panel */}
      <mesh position={[0, 1.15, 0]} castShadow receiveShadow>
        <boxGeometry args={[1, 2.3, 0.1]} />
        <meshStandardMaterial
          color={colors.wood || colors.accent}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function AnimatedDoorModel({ world, interactive, hovered, objectId, isOccluding, opacity = 1 }: ModelProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;
  const { openedDoors } = useGameStore();
  const doorRef = useRef<Group>(null);
  const doorRotation = useRef(0);
  const isDoorOpen = objectId ? openedDoors.has(objectId) : false;

  useFrame((_, delta) => {
    if (doorRef.current) {
      const targetRotation = isDoorOpen ? -Math.PI / 2 : 0;
      doorRotation.current += (targetRotation - doorRotation.current) * delta * 3;
      doorRef.current.rotation.y = doorRotation.current;
    }
  });

  return (
    <group>
      {/* Frame - stone pillars */}
      <mesh position={[-0.6, 1.25, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.15, 2.5, 0.4]} />
        <meshStandardMaterial color={colors.stone} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0.6, 1.25, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.15, 2.5, 0.4]} />
        <meshStandardMaterial color={colors.stone} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      <mesh position={[0, 2.65, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.35, 0.25, 0.4]} />
        <meshStandardMaterial color={colors.stone} roughness={0.8} transparent={isOccluding} opacity={opacity} />
      </mesh>
      {/* Door panel - pivots from left edge */}
      <group ref={doorRef} position={[-0.5, 0, 0]}>
        <mesh position={[0.5, 1.15, 0]} castShadow receiveShadow>
          <boxGeometry args={[1, 2.3, 0.12]} />
          <meshStandardMaterial
            color={world === 'real' ? '#8B4513' : '#5a3a2a'}
            roughness={0.7}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
        {/* Door handle */}
        <mesh position={[0.85, 1.15, 0.1]} castShadow>
          <sphereGeometry args={[0.06, 8, 8]} />
          <meshStandardMaterial
            color={colors.gold || '#d4af37'}
            metalness={0.8}
            roughness={0.2}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
        {/* Door decorations */}
        <mesh position={[0.5, 1.6, 0.07]} castShadow>
          <boxGeometry args={[0.6, 0.08, 0.02]} />
          <meshStandardMaterial color={colors.gold || '#d4af37'} metalness={0.6} roughness={0.3} transparent={isOccluding} opacity={opacity} />
        </mesh>
        <mesh position={[0.5, 0.7, 0.07]} castShadow>
          <boxGeometry args={[0.6, 0.08, 0.02]} />
          <meshStandardMaterial color={colors.gold || '#d4af37'} metalness={0.6} roughness={0.3} transparent={isOccluding} opacity={opacity} />
        </mesh>
      </group>
      {/* Visual indicator when door is closed and interactive */}
      {!isDoorOpen && interactive && hovered && (
        <pointLight position={[0, 1.5, 0.5]} color="#ffd700" intensity={2} distance={3} />
      )}
    </group>
  );
}

export function SwitchModel({ world, hovered }: ModelProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;
  return (
    <group>
      {/* Base */}
      <mesh position={[0, 0.1, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.3, 0.35, 0.2, 8]} />
        <meshStandardMaterial color={colors.stone} roughness={0.8} />
      </mesh>
      {/* Lever */}
      <mesh
        position={[0, 0.4, 0]}
        rotation={[hovered ? 0.3 : 0, 0, 0]}
        castShadow
      >
        <cylinderGeometry args={[0.05, 0.05, 0.5, 8]} />
        <meshStandardMaterial
          color={hovered ? colors.gold || colors.highlight : colors.accent}
          roughness={0.5}
        />
      </mesh>
    </group>
  );
}

export function WallModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.75, 0]} castShadow receiveShadow>
        <boxGeometry args={[1, 1.5, 1]} />
        <meshStandardMaterial
          color={world === 'real' ? '#8a8a7a' : '#5a5a6a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Top decoration */}
      <mesh position={[0, 1.6, 0]} castShadow>
        <boxGeometry args={[1.1, 0.15, 1.1]} />
        <meshStandardMaterial
          color={world === 'real' ? '#7a7a6a' : '#4a4a5a'}
          roughness={0.85}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function WallCornerModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  return (
    <group>
      <mesh position={[0, 0.75, 0]} castShadow receiveShadow>
        <boxGeometry args={[1, 1.5, 1]} />
        <meshStandardMaterial
          color={world === 'real' ? '#9a9a8a' : '#6a6a7a'}
          roughness={0.9}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Pillar accent */}
      <mesh position={[0, 0.85, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.18, 1.7, 8]} />
        <meshStandardMaterial
          color={world === 'real' ? '#7a7a6a' : '#4a4a5a'}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function PlatformModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;
  return (
    <group>
      {/* Main platform body */}
      <mesh position={[0, 0.15, 0]} castShadow receiveShadow>
        <boxGeometry args={[1, 0.3, 1]} />
        <meshStandardMaterial
          color={colors.stone}
          roughness={0.8}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
      {/* Top edge */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <boxGeometry args={[1.05, 0.08, 1.05]} />
        <meshStandardMaterial
          color={world === 'real' ? '#9a9a8a' : '#5a5a6a'}
          roughness={0.7}
          transparent={isOccluding}
          opacity={opacity}
        />
      </mesh>
    </group>
  );
}

export function StairsModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;
  return (
    <group>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} position={[0, i * 0.25 + 0.125, i * 0.25 - 0.375]} castShadow receiveShadow>
          <boxGeometry args={[1, 0.25, 0.25]} />
          <meshStandardMaterial
            color={colors.stone}
            roughness={0.8}
            transparent={isOccluding}
            opacity={opacity}
          />
        </mesh>
      ))}
    </group>
  );
}

export function RampModel({ world, isOccluding, opacity = 1 }: ModelProps) {
  const colors = world === 'real' ? COLORS.real : COLORS.spectral;
  return (
    <mesh position={[0, 0.25, 0]} rotation={[Math.PI / 8, 0, 0]} castShadow receiveShadow>
      <boxGeometry args={[1, 0.15, 1.5]} />
      <meshStandardMaterial
        color={colors.stone}
        roughness={0.8}
        transparent={isOccluding}
        opacity={opacity}
      />
    </mesh>
  );
}

export function BridgeModel({ world }: ModelProps) {
  return (
    <group position={[0, 0.1, 0]}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[2, 0.15, 1]} />
        <meshStandardMaterial
          color={world === 'spectral' ? '#7fb3d5' : '#3a4a5a'}
          emissive={world === 'spectral' ? '#5a8ab5' : undefined}
          emissiveIntensity={world === 'spectral' ? 0.4 : 0}
          transparent
          opacity={world === 'spectral' ? 0.9 : 0.2}
          roughness={0.3}
        />
      </mesh>
      {/* Glow effect in spectral */}
      {world === 'spectral' && (
        <pointLight position={[0, 0.5, 0]} color="#7fb3d5" intensity={3} distance={4} />
      )}
    </group>
  );
}

export function PitModel() {
  return (
    <mesh position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <circleGeometry args={[0.8, 8]} />
      <meshStandardMaterial
        color="#1a1a2a"
        roughness={1}
      />
    </mesh>
  );
}

export const structuralModels: ModelRegistry = {
  cube: CubeModel,
  block: CubeModel,
  pillar: PillarModel,
  arch: ArchModel,
  door: DoorModel,
  animated_door: AnimatedDoorModel,
  switch: SwitchModel,
  wall: WallModel,
  wall_corner: WallCornerModel,
  platform: PlatformModel,
  stairs: StairsModel,
  ramp: RampModel,
  bridge: BridgeModel,
  spectral_bridge: BridgeModel,
  pit: PitModel,
  hole: PitModel,
};
